import React from 'react';
import { useTheme } from '../../lib/theme/useTheme';
import type { ThemePreference } from '../../lib/theme/theme';

/**
 * Segmented System / Light / Dark control.
 *
 * 'System' follows the OS and is the default; 'Light' / 'Dark' pin the theme
 * permanently (persisted to localStorage). The active segment reflects the
 * user's *preference*, not the resolved theme, so 'System' stays highlighted
 * even when the OS is currently dark.
 */

const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; icon: React.ReactNode }> = [
  { value: 'system', label: 'System', icon: <MonitorIcon /> },
  { value: 'light', label: 'Light', icon: <SunIcon /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon /> },
];

export const ThemeToggle: React.FC = () => {
  const { preference, setPreference } = useTheme();

  return (
    <div
      role="group"
      aria-label="Theme"
      data-testid="theme-toggle"
      className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5"
    >
      {OPTIONS.map((opt) => {
        const active = preference === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            title={`${opt.label} theme`}
            aria-label={`${opt.label} theme`}
            aria-pressed={active}
            data-testid={`theme-option-${opt.value}`}
            onClick={() => setPreference(opt.value)}
            className={
              'inline-flex h-6 w-7 items-center justify-center rounded-md transition-colors ' +
              (active
                ? 'bg-white text-primary-600 shadow-card dark:bg-primary-500/30 dark:text-[#EDF1F7]'
                : 'text-slate-500 hover:text-slate-800')
            }
          >
            {opt.icon}
          </button>
        );
      })}
    </div>
  );
};

// ── Icons (inline so we add no dependency) ───────────────────────────────────
const ICON = 'h-3.5 w-3.5';

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className={ICON} aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className={ICON} aria-hidden>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className={ICON} aria-hidden>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

export default ThemeToggle;
